// Spreadsheet State Engine APIs
import { getApiClient, withOrgConfig } from '../quote-api-helpers'
import type {
  QuoteSpreadsheetResponse, QuoteSpreadsheetInitResponse,
  QuoteSpreadsheetEditResponse, QuoteSpreadsheetSyncResponse,
  QuoteSpreadsheetBindingsResponse, QuoteSpreadsheetBindingsApplyResponse,
  QuoteSpreadsheetHistoryResponse, QuoteSpreadsheetCompareResponse,
  QuoteSpreadsheetRevertResponse, QuoteSpreadsheetAutoSyncResponse,
  NamedRangeBinding,
} from '../quote-api-spreadsheet-types'

export const spreadsheetApi = {
  /**
   * Get the spreadsheet render model for a quote.
   * Returns Excel representation with authoritative calculated values.
   */
  async getQuoteSpreadsheet(quoteId: string): Promise<QuoteSpreadsheetResponse> {
    const client = getApiClient()
    const res = await client.get<QuoteSpreadsheetResponse>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/`,
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Initialize a quote's spreadsheet from an Excel URL.
   * Triggers parsing and creates a WorkbookRevision.
   */
  async initQuoteSpreadsheet(
    quoteId: string,
    excelUrl: string
  ): Promise<QuoteSpreadsheetInitResponse> {
    const client = getApiClient()
    const res = await client.post<QuoteSpreadsheetInitResponse>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/`,
      { excel_url: excelUrl },
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Upload an Excel file to initialize/replace a quote's spreadsheet.
   * Creates a new WorkbookRevision and triggers parsing.
   */
  async uploadQuoteSpreadsheet(
    quoteId: string,
    file: File
  ): Promise<QuoteSpreadsheetResponse> {
    const client = getApiClient()
    const formData = new FormData()
    formData.append('file', file)

    const res = await client.post<QuoteSpreadsheetResponse>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/upload/`,
      formData,
      withOrgConfig({
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    )
    return res.data
  },

  /**
   * Apply cell edits to a quote's spreadsheet.
   * Creates new WorkbookRevision and triggers recalculation.
   *
   * @param expectedRevisionId - Optional. For optimistic concurrency, pass the current
   *                             revision ID to detect concurrent edits. If revision
   *                             mismatch, throws RevisionConflictError.
   */
  async editQuoteSpreadsheet(
    quoteId: string,
    edits: Array<{ sheet: string; cell: string; value: string | number | null }>,
    editDescription?: string,
    expectedRevisionId?: string
  ): Promise<QuoteSpreadsheetEditResponse> {
    const client = getApiClient()

    try {
      const res = await client.put<QuoteSpreadsheetEditResponse>(
        `/quotebuilder/quotes/${quoteId}/spreadsheet/edit/`,
        {
          edits,
          edit_description: editDescription || '',
          expected_revision_id: expectedRevisionId,
        },
        withOrgConfig()
      )
      return res.data
    } catch (error: unknown) {
      // Handle 409 Conflict specifically
      const axiosErr = error as Record<string, unknown>
      const response = axiosErr.response as Record<string, unknown> | undefined
      const responseData = response?.data as Record<string, unknown> | undefined
      if (response?.status === 409 && responseData?.code === 'REVISION_CONFLICT') {
        const conflictError = new Error('Revision conflict - the workbook has been modified by someone else') as Error & Record<string, unknown>
        conflictError.isConflict = true
        conflictError.currentRevisionId = responseData.current_revision_id
        conflictError.expectedRevisionId = responseData.expected_revision_id
        throw conflictError
      }
      throw error
    }
  },

  /**
   * Sync structured Quote data from the spreadsheet.
   * Updates Quote fields from NamedRangeBindings.
   */
  async syncQuoteFromSpreadsheet(quoteId: string): Promise<QuoteSpreadsheetSyncResponse> {
    const client = getApiClient()
    const res = await client.post<QuoteSpreadsheetSyncResponse>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/sync/`,
      {},
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Get named range bindings for a quote's spreadsheet.
   * Returns binding definitions and current values.
   */
  async getQuoteSpreadsheetBindings(quoteId: string): Promise<QuoteSpreadsheetBindingsResponse> {
    const client = getApiClient()
    const res = await client.get<QuoteSpreadsheetBindingsResponse>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/bindings/`,
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Apply values through named range bindings.
   * Triggers recalculation after applying values.
   */
  async applyQuoteSpreadsheetBindings(
    quoteId: string,
    bindingValues: Record<string, string | number | null>
  ): Promise<QuoteSpreadsheetBindingsApplyResponse> {
    const client = getApiClient()
    const res = await client.post<QuoteSpreadsheetBindingsApplyResponse>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/bindings/`,
      bindingValues,
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Update a named range binding's metadata.
   * @param quoteId - Quote UUID
   * @param bindingName - Name of the binding to update
   * @param updates - Fields to update (direction, data_type, description, required, domain_path)
   */
  async updateQuoteSpreadsheetBinding(
    quoteId: string,
    bindingName: string,
    updates: {
      direction?: 'input' | 'output' | 'bidirectional' | 'computed'
      data_type?: 'text' | 'number' | 'currency' | 'percent' | 'date' | 'boolean'
      description?: string
      required?: boolean
      domain_path?: string
    }
  ): Promise<{ message: string; binding: NamedRangeBinding; updated_fields: string[] }> {
    const client = getApiClient()
    const res = await client.put<{ message: string; binding: NamedRangeBinding; updated_fields: string[] }>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/bindings/${bindingName}/`,
      updates,
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Delete a named range binding.
   * @param quoteId - Quote UUID
   * @param bindingName - Name of the binding to delete
   */
  async deleteQuoteSpreadsheetBinding(
    quoteId: string,
    bindingName: string
  ): Promise<{ message: string; binding_name: string }> {
    const client = getApiClient()
    const res = await client.delete<{ message: string; binding_name: string }>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/bindings/${bindingName}/`,
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Get spreadsheet revision history for a quote.
   * @param quoteId - Quote UUID
   */
  async getQuoteSpreadsheetHistory(quoteId: string): Promise<QuoteSpreadsheetHistoryResponse> {
    const client = getApiClient()
    const res = await client.get<QuoteSpreadsheetHistoryResponse>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/history/`,
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Compare two spreadsheet revisions.
   * @param quoteId - Quote UUID
   * @param revisionA - First revision UUID
   * @param revisionB - Second revision UUID
   */
  async compareQuoteSpreadsheetRevisions(
    quoteId: string,
    revisionA: string,
    revisionB: string
  ): Promise<QuoteSpreadsheetCompareResponse> {
    const client = getApiClient()
    const res = await client.get<QuoteSpreadsheetCompareResponse>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/compare/`,
      {
        ...withOrgConfig(),
        params: {
          revision_a: revisionA,
          revision_b: revisionB,
        },
      }
    )
    return res.data
  },

  /**
   * Revert to a specific revision (Undo/Redo support).
   * Creates a new revision based on the target revision's workbook content.
   * @param quoteId - Quote UUID
   * @param revisionId - Target revision UUID to revert to
   * @param description - Optional description for the revert
   */
  async revertToRevision(
    quoteId: string,
    revisionId: string,
    description?: string
  ): Promise<QuoteSpreadsheetRevertResponse> {
    const client = getApiClient()
    const res = await client.post<QuoteSpreadsheetRevertResponse>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/revert/`,
      {
        revision_id: revisionId,
        description,
      },
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Auto-sync Quote fields to spreadsheet bindings.
   * Pushes values like client_name, project_name, etc. to the Excel spreadsheet
   * via named range bindings (input/bidirectional).
   *
   * @param quoteId - Quote UUID
   * @param fields - Optional: specific fields to sync. If omitted, syncs all mapped fields.
   */
  async autoSyncToSpreadsheet(
    quoteId: string,
    fields?: string[]
  ): Promise<QuoteSpreadsheetAutoSyncResponse> {
    const client = getApiClient()
    const res = await client.post<QuoteSpreadsheetAutoSyncResponse>(
      `/quotebuilder/quotes/${quoteId}/spreadsheet/auto-sync/`,
      fields ? { fields } : {},
      withOrgConfig()
    )
    return res.data
  },
}
